import * as React from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/feedback";
import { ApiError, hasStoredSession, isTabSession } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { tokenFromHash } from "@/lib/invitations";
import { landingPath } from "@/lib/nav";
import { Notice, Shell } from "@/routes/invite";

/**
 * The page an admin's sign-in link opens (ADR-040 in `platform`), `/sign-in-as#<token>`: this
 * browser becomes the person the link was made for. The token is in the fragment, which a browser
 * never sends to a server, so it reaches core-api only in the body of the one request made here.
 *
 * It signs in at once. A browser with nobody signed in gets the session on its cookie, as a login
 * does. A browser that already has a session keeps it: the session is this tab's alone (`lib/api.ts`),
 * which is what "Open in a new tab" on the Users page relies on. Either way the console then shows
 * whose it is, with Exit (`features/signed-in-as-bar.tsx`).
 *
 * Signing in remounts everything under `AuthProvider`, this page included, at its start when there
 * was a session and again once it has one. So the attempt lives outside the page, keyed by token:
 * a remounted page picks up the sign-in in flight rather than spending the link a second time, and
 * StrictMode's second effect finds it too.
 */
export function SignInAsPage() {
  const { user, loading, signInWithLink } = useAuth();
  const { hash } = useLocation();
  const token = tokenFromHash(hash);
  const [attempt, start] = useAttempt(token, signInWithLink);

  // Read once, before the sign-in clears anything: whether this browser already has a session.
  // A page remounted by a tab sign-in already under way reads that from `isTabSession`.
  const [inTab] = React.useState(() => user !== null || hasStoredSession() || isTabSession());
  const automatic = !loading && Boolean(token) && attempt === undefined;
  React.useEffect(() => {
    if (automatic) start(false, inTab);
  }, [automatic, inTab, start]);

  if (loading) return null;

  if (!token) {
    return (
      <Shell title="Sign in as somebody">
        <Notice
          title="This link is incomplete"
          body="Copy the whole sign-in link again, or make a new one from the Users page."
        />
      </Shell>
    );
  }

  if (attempt?.state === "failed") {
    const refused = attempt.error instanceof ApiError;
    return (
      <Shell title="Sign in as somebody">
        <Notice
          title={refused ? "This link cannot sign you in" : "Could not sign in"}
          body={
            refused
              ? (attempt.error as ApiError).message
              : "Could not reach the server. Check your connection and try again."
          }
          action={
            !refused ? (
              <Button variant="outline" onClick={() => start(true, inTab)}>
                Try again
              </Button>
            ) : isTabSession() ? (
              // The browser's own session was never touched: a reload is back in it.
              <Button variant="outline" onClick={() => window.location.assign("/")}>
                Back to your console
              </Button>
            ) : (
              <Button variant="outline" asChild>
                <Link to="/login">Go to sign in</Link>
              </Button>
            )
          }
        />
      </Shell>
    );
  }

  if (attempt?.state === "done" && user) {
    return <Navigate to={landingPath(user.role)} replace />;
  }

  return (
    <Shell title="Sign in as somebody">
      <div className="flex items-center justify-center gap-2 py-6 text-xs text-text-muted">
        <Spinner className="size-4" />
        Signing in…
      </div>
    </Shell>
  );
}

type Attempt = { state: "pending" } | { state: "done" } | { state: "failed"; error: unknown };

/** Sign-ins started from this page, by token, for the life of the tab. */
const attempts = new Map<string, { promise: Promise<void>; result: Attempt }>();

/** Exported for the page's tests, which open one link in many fresh pages. */
export function forgetSignInAttempts(): void {
  attempts.clear();
}

function useAttempt(
  token: string,
  signInWithLink: (token: string, tab?: boolean) => Promise<void>,
): [Attempt | undefined, (again?: boolean, tab?: boolean) => void] {
  const [, rerender] = React.useReducer((n: number) => n + 1, 0);
  const current = token ? attempts.get(token) : undefined;

  React.useEffect(() => {
    if (current?.result.state !== "pending") return;
    let live = true;
    const settle = () => { if (live) rerender(); };
    current.promise.then(settle, settle);
    return () => { live = false; };
  }, [current]);

  const start = React.useCallback((again = false, tab = false) => {
    if (!token || (attempts.has(token) && !again)) return;
    const entry: { promise: Promise<void>; result: Attempt } = {
      promise: Promise.resolve(),
      result: { state: "pending" },
    };
    // In the map before the sign-in starts, since starting it can already remount the page.
    attempts.set(token, entry);
    entry.promise = signInWithLink(token, tab).then(
      () => { entry.result = { state: "done" }; },
      (error: unknown) => { entry.result = { state: "failed", error }; },
    );
    rerender();
  }, [token, signInWithLink]);

  return [current?.result, start];
}
